import { useStorage } from '@vueuse/core';
import { defineStore } from 'pinia';

import {Howl, Howler} from 'howler';

export const Audio = defineStore('audio', {
  state: () => ({
    // history
    history: useStorage('demo_audio_history', []),

    // player state
    sound: null,
    isPlaying: false,
    currentTime: 0,
    duration: 0,
    volume: 0.7,
    isMuted: false,
    
    // next and playlist
    currentTrack: null,
    playlist: [],
    currentTrackIndex: -1,
    
    // progress
    progress: 0,
    isLoading: false,
    error: null,
    
    // tabs sync
    syncEnabled: true,
    lastSyncTime: 0
  }),

  getters: {
    // Геттеры для удобства
    formattedCurrentTime: (state) => {
      const minutes = Math.floor(state.currentTime / 60)
      const seconds = Math.floor(state.currentTime % 60)
      return `${minutes}:${seconds.toString().padStart(2, '0')}`
    },
    
    formattedDuration: (state) => {
      if (!state.duration) return '0:00'
      const minutes = Math.floor(state.duration / 60)
      const seconds = Math.floor(state.duration % 60)
      return `${minutes}:${seconds.toString().padStart(2, '0')}`
    },
    
    nextTrack: (state) => {
      if (state.playlist.length === 0 || state.currentTrackIndex === -1) return null
      const nextIndex = (state.currentTrackIndex + 1) % state.playlist.length
      return state.playlist[nextIndex]
    },
    
    previousTrack: (state) => {
      if (state.playlist.length === 0 || state.currentTrackIndex === -1) return null
      const prevIndex = (state.currentTrackIndex - 1 + state.playlist.length) % state.playlist.length
      return state.playlist[prevIndex]
    },
    
    hasNextTrack: (state) => {
      return state.playlist.length > 0 && state.playlist.length > 1
    },
    
    hasPreviousTrack: (state) => {
      return state.playlist.length > 0 && state.playlist.length > 1
    }
  },

  actions: {
    // Инициализация плеера
    initialize() {
      // Восстановление состояния из localStorage
      this.restoreState()
      
      // Настройка синхронизации между вкладками
      this.setupTabSync()
      
      // Автосохранение состояния
      this.setupAutoSave()
    },

    // Настройка синхронизации между вкладками
    setupTabSync() {
      // Создаем BroadcastChannel для обмена сообщениями
      this.broadcastChannel = new BroadcastChannel('audio_player_sync')
      
      // Слушаем сообщения от других вкладок
      this.broadcastChannel.onmessage = (event) => {
        const { type, payload } = event.data
        
        switch (type) {
          case 'PLAY':
            if (payload.trackId !== this.currentTrack?.id) {
              const track = this.playlist.find(t => t.id === payload.trackId)
              if (track) {
                this.loadTrack(track)
              }
            }
            this.play()
            break
            
          case 'PAUSE':
            this.pause()
            break
            
          case 'SEEK':
            this.seek(payload.time)
            break
            
          case 'VOLUME':
            this.setVolume(payload.volume)
            break
            
          case 'SYNC_REQUEST':
            // Отправляем текущее состояние по запросу
            this.broadcastState()
            break
            
          case 'SYNC_STATE':
            // Синхронизируемся с полученным состоянием
            this.syncWithState(payload)
            break
        }
      }
      
      // Запрашиваем синхронизацию при загрузке
      this.broadcastChannel.postMessage({ type: 'SYNC_REQUEST' })
    },

    // Загрузка трека
    loadTrack(track, autoplay = false) {
      if (this.sound) {
        this.sound.unload()
      }
      
      this.isLoading = true
      this.error = null
      this.currentTrack = track
      this.currentTrackIndex = this.playlist.findIndex(t => t.id === track.id)
      
      // Создаем новый Howl инстанс
      this.sound = new Howl({
        src: [track.url],
        html5: true, // Используем HTML5 Audio для лучшей производительности
        preload: true,
        volume: this.volume,
        mute: this.isMuted,
        
        onload: () => {
          this.duration = this.sound.duration()
          this.isLoading = false
          if (autoplay) {
            this.play()
          }
          this.saveState()
          this.broadcastState()
        },
        
        onloaderror: (id, error) => {
          this.error = `Ошибка загрузки: ${error}`
          this.isLoading = false
        },
        
        onplay: () => {
          this.isPlaying = true
          this.startProgressTimer()
          this.broadcastAction('PLAY', { trackId: track.id })
        },
        
        onpause: () => {
          this.isPlaying = false
          this.stopProgressTimer()
          this.broadcastAction('PAUSE')
        },
        
        onstop: () => {
          this.isPlaying = false
          this.currentTime = 0
          this.progress = 0
          this.stopProgressTimer()
        },
        
        onend: () => {
          this.isPlaying = false
          this.currentTime = 0
          this.progress = 0
          this.stopProgressTimer()
          
          // Автопереход к следующему треку
          if (this.nextTrack && this.playlist.length > 1) {
            this.loadTrack(this.nextTrack, true)
          }
        },
        
        onseek: () => {
          this.currentTime = this.sound.seek()
          this.progress = (this.currentTime / this.duration) * 100
        }
      })
    },

    // Воспроизведение
    play() {
      if (!this.sound) return
      
      if (this.sound.playing()) {
        this.sound.pause()
      } else {
        this.sound.play()
      }
      
      this.saveState()
    },

    // Пауза
    pause() {
      if (this.sound && this.sound.playing()) {
        this.sound.pause()
        this.broadcastAction('PAUSE')
      }
    },

    // Остановка
    stop() {
      if (this.sound) {
        this.sound.stop()
        this.currentTime = 0
        this.progress = 0
        this.isPlaying = false
        this.broadcastAction('STOP')
      }
    },

    // Перемотка
    seek(time) {
      if (this.sound) {
        this.sound.seek(time)
        this.currentTime = time
        this.progress = (time / this.duration) * 100
        this.broadcastAction('SEEK', { time })
      }
    },

    // Установка громкости
    setVolume(volume) {
      this.volume = Math.max(0, Math.min(1, volume))
      if (this.sound) {
        this.sound.volume(this.volume)
      }
      this.broadcastAction('VOLUME', { volume: this.volume })
      this.saveState()
    },

    // Включение/выключение звука
    toggleMute() {
      this.isMuted = !this.isMuted
      if (this.sound) {
        this.sound.mute(this.isMuted)
      }
      this.saveState()
    },

    // Следующий трек
    next() {
      if (this.hasNextTrack) {
        this.loadTrack(this.nextTrack, true)
      }
    },

    // Предыдущий трек
    previous() {
      if (this.hasPreviousTrack) {
        this.loadTrack(this.previousTrack, true)
      }
    },

    // Добавление треков в плейлист
    addToPlaylist(tracks) {
      this.playlist.push(...tracks)
      this.saveState()
    },

    // Очистка плейлиста
    clearPlaylist() {
      this.stop()
      this.playlist = []
      this.currentTrack = null
      this.currentTrackIndex = -1
      this.saveState()
    },

    // Установка плейлиста
    setPlaylist(tracks) {
      this.playlist = tracks
      this.saveState()
    },

    // Таймер для обновления прогресса
    startProgressTimer() {
      this.stopProgressTimer()
      this.progressTimer = setInterval(() => {
        if (this.sound && this.sound.playing()) {
          this.currentTime = this.sound.seek()
          this.progress = (this.currentTime / this.duration) * 100
        }
      }, 100)
    },

    stopProgressTimer() {
      if (this.progressTimer) {
        clearInterval(this.progressTimer)
        this.progressTimer = null
      }
    },

    // Сохранение состояния в localStorage
    saveState() {
      const state = {
        volume: this.volume,
        isMuted: this.isMuted,
        playlist: this.playlist,
        currentTrack: this.currentTrack,
        currentTrackIndex: this.currentTrackIndex,
        currentTime: this.currentTime,
        isPlaying: this.isPlaying
      }
      localStorage.setItem('audioPlayerState', JSON.stringify(state))
    },

    // Восстановление состояния из localStorage
    restoreState() {
      try {
        const saved = localStorage.getItem('audioPlayerState')
        if (saved) {
          const state = JSON.parse(saved)
          
          this.volume = state.volume || this.volume
          this.isMuted = state.isMuted || this.isMuted
          this.playlist = state.playlist || this.playlist
          this.currentTrack = state.currentTrack || this.currentTrack
          this.currentTrackIndex = state.currentTrackIndex || this.currentTrackIndex
          this.currentTime = state.currentTime || this.currentTime
          
          // Воспроизводим если было воспроизведение
          if (state.isPlaying && this.currentTrack) {
            setTimeout(() => {
              this.loadTrack(this.currentTrack, true)
              if (this.sound) {
                this.sound.seek(this.currentTime)
              }
            }, 100)
          }
        }
      } catch (error) {
        console.error('Ошибка восстановления состояния:', error)
      }
    },

    // Настройка автосохранения
    setupAutoSave() {
      this.autoSaveInterval = setInterval(() => {
        this.saveState()
      }, 30000) // Сохраняем каждые 30 секунд
    },

    // Отправка действия в другие вкладки
    broadcastAction(type, payload = {}) {
      if (!this.syncEnabled || !this.broadcastChannel) return
      
      this.broadcastChannel.postMessage({
        type,
        payload: {
          ...payload,
          timestamp: Date.now()
        }
      })
    },

    // Отправка полного состояния
    broadcastState() {
      if (!this.syncEnabled || !this.broadcastChannel) return
      
      this.broadcastChannel.postMessage({
        type: 'SYNC_STATE',
        payload: {
          volume: this.volume,
          isMuted: this.isMuted,
          currentTrack: this.currentTrack,
          currentTime: this.currentTime,
          isPlaying: this.isPlaying,
          playlist: this.playlist,
          timestamp: Date.now()
        }
      })
    },

    // Синхронизация с состоянием из другой вкладки
    syncWithState(state) {
      // Игнорируем устаревшие синхронизации
      if (state.timestamp < this.lastSyncTime) return
      
      this.lastSyncTime = state.timestamp
      
      // Обновляем состояние
      this.volume = state.volume
      this.isMuted = state.isMuted
      this.playlist = state.playlist
      
      // Если текущий трек отличается
      if (state.currentTrack && 
          (!this.currentTrack || state.currentTrack.id !== this.currentTrack.id)) {
        this.loadTrack(state.currentTrack, state.isPlaying)
      }
      
      // Синхронизируем время и состояние воспроизведения
      if (this.sound) {
        if (Math.abs(state.currentTime - this.currentTime) > 2) {
          this.seek(state.currentTime)
        }
        
        if (state.isPlaying !== this.isPlaying) {
          if (state.isPlaying) {
            this.sound.play()
          } else {
            this.sound.pause()
          }
        }
      }
    },

    // Очистка ресурсов
    cleanup() {
      this.stopProgressTimer()
      if (this.autoSaveInterval) {
        clearInterval(this.autoSaveInterval)
      }
      if (this.broadcastChannel) {
        this.broadcastChannel.close()
      }
      if (this.sound) {
        this.sound.unload()
      }
    }
  }
})